import { createSlice, createSelector } from 'redux-starter-kit'
import * as auth from '../auth'
import { loadState, migrate } from './localStorage'
import messages from './messages'
import {
  reset as resetTracking,
  trackLogin,
  trackLogOut,
  trackCompleteRegistration,
  trackErrorMessage,
} from '../analytics'
import { getSessionItem } from '../storeManager'

import differenceInYears from 'date-fns/difference_in_years'
import {
  USER_NOT_FOUND,
  USER_CANCELLED,
  UNKNOWN_ERROR,
  INVALID_REQUEST,
} from '../apple'

function isEmpty(obj) {
  if (obj == null) {
    return true
  }

  return !Object.keys(obj).length === 0
}

const defaultState = {
  errors: {},
  isLoggedIn: false,
  isLoggingIn: false,
  isDeleted: false,
  isDeleting: false,
  isCancelling: false,
  isCreating: false,
  isVerifying: false,
  isUpdating: false,
  isLoadingLimitedProfile: false,
  userToken: undefined,
  profile: {},
}

export const migrations = {
  1: state => {
    // Migrate from 0 to 1
    const { userData, userId, ...oldState } = state
    delete userData.isCancelled
    return {
      ...oldState,
      profile: oldState.isLoggedIn
        ? {
            ...userData,
            id: userId,
            country: null,
            gender: null,
            image: null,
          }
        : {},
    }
  },
  2: state => {
    const { birthdate, paymentMeta, ...profile } = state.profile
    return {
      ...state,
      profile: isEmpty(profile)
        ? {}
        : {
            ...profile,
            birthDate: birthdate,
            paymentMeta: isEmpty(paymentMeta)
              ? undefined
              : {
                  cardSummary: paymentMeta.cardSummary,
                  expires: paymentMeta.expires,
                  paymentMethod: paymentMeta.paymentMethod,
                  cardType: paymentMeta.cardType,
                  nameOnCard: paymentMeta.nameOnCard,
                },
          },
    }
  },
  3: state => {
    if (state.isCancelled) {
      delete state.isCancelled
    }
    return state
  },
}

const previousState = loadState()

const initialState = {
  ...Object.assign(
    {},
    defaultState,
    previousState && previousState.user
      ? migrate(migrations)(previousState.user)
      : {}
  ),
  errors: {},
  isDeleted: false,
  isLoggingIn: false,
  isDeleting: false,
  isCancelling: false,
  isCreating: false,
  isVerifying: false,
  __version: 3,
}

export const mapApiProfileToObject = apiProfile => {
  return {
    autoRenewing: !!apiProfile.autorenewing,
    birthDate: apiProfile.birthdate,
    hasCancelled: !!apiProfile.cancelled,
    country: apiProfile.country,
    email: apiProfile.email,
    expiresTime: apiProfile.premium_purchase_time
      ? apiProfile.premium_expires_time
      : apiProfile.next_charge_date,
    firstName: apiProfile.firstname,
    gender: apiProfile.gender,
    lastName: apiProfile.lastname,
    paymentMeta:
      apiProfile.payment_meta || apiProfile.card_info
        ? apiProfile.card_info
          ? {
              cardSummary: apiProfile.card_info.card_summary,
              cardType: apiProfile.card_info.card_type
                ? apiProfile.card_info.card_type
                : false,
              expires: apiProfile.card_info.expiry_date,
              nameOnCard: apiProfile.card_info.name,
            }
          : {
              cardSummary: apiProfile.payment_meta.card_summary,
              cardType: false,
              expires: apiProfile.payment_meta.expires,
              nameOnCard: false,
            }
        : {},
    paymentMethod: apiProfile.payment_meta
      ? apiProfile.payment_meta.payment_method
      : apiProfile.payment_method,
    isPremium: apiProfile.premium,
    image: apiProfile.profile_image,
    facebookImage: apiProfile.facebook_profile_image,
    purchaseTime: apiProfile.premium_purchase_time,
    purchaseType: apiProfile.premium_purchase_type,
    isRefunded: !!apiProfile.refunded,
    store: apiProfile.store,
    id: apiProfile.userid,
    currencyLocal:
      apiProfile.next_charge_price && apiProfile.next_charge_price.currency,
    nextPaymentAmountLocal:
      apiProfile.next_charge_price && apiProfile.next_charge_price.value,
    isNewStyleSubscription: apiProfile.new_style_subscription,
    status: apiProfile.status,
  }
}

const slice = createSlice({
  slice: 'user',
  initialState,
  reducers: {
    setUserToken(state, { payload: token }) {
      state.userToken = token // TODO: do we have to refresh this at some point?
    },
    setUserId(state, { payload: id }) {
      state.profile = {
        ...state.profile,
        id,
      }
    },
    setUserProfile(state, { payload: profile }) {
      state.profile = profile
    },
    mergeUserProfile(state, { payload: profile }) {
      state.profile = { ...state.profile, ...profile }
    },
    logOut(state) {
      trackLogOut()
      resetTracking()

      return {
        ...state,
        isLoggedIn: false,
        isDeleted: false,
        userToken: undefined,
        profile: {},
      }
    },
    reset(state) {
      return {
        __version: state.__version,
        ...defaultState,
      }
    },
    upgradeToPremium(state) {
      state.profile.isPremium = true
    },
    downgradeToFree(state) {
      state.profile.isPremium = false
    },
    logInRequest(state) {
      state.isLoggedIn = false
      state.isLoggingIn = true
      state.isDeleted = false
      state.isDeleting = false
      state.isCancelling = false
    },
    logInSuccess(state) {
      state.isLoggedIn = true
      state.isLoggingIn = false
      state.isDeleted = false
      state.isDeleting = false
      state.isCancelling = false
    },
    logInFailure(state) {
      state.isLoggedIn = false
      state.isLoggingIn = false
      state.isDeleted = false
      state.isDeleting = false
      state.isCancelling = false
      state.userToken = ''
      state.profile = {}
    },
    deleteRequest(state) {
      state.isDeleted = false
      state.isDeleting = true
    },
    deleteSuccess(state) {
      state.isDeleted = true
      state.isDeleting = false
    },
    deleteFailure(state) {
      state.isDeleting = false
    },
    cancelRequest(state) {
      state.isCancelling = true
      state.errors.cancel = undefined
    },
    cancelSuccess(state) {
      state.isCancelling = false
      state.errors.cancel = undefined
    },
    cancelFailure(state, { payload: errorMessage }) {
      state.isCancelling = false
      state.errors.cancel = errorMessage
    },
    createRequest(state) {
      state.isCreating = true
      state.errors.create = undefined
    },
    createSuccess(state) {
      state.isCreating = false
      state.isLoggedIn = true
      state.errors.create = undefined
    },
    createFailure(state, { payload: errorMessage }) {
      state.isCreating = false
      state.errors.create = errorMessage
    },
    verifyRequest(state) {
      state.isVerifying = true
      state.errors.verify = undefined
    },
    verifySuccess(state) {
      state.isVerifying = false
      state.errors.verify = undefined
      state.isLoggedIn = true
    },
    verifyFailure(state, { payload: errorMessage }) {
      state.isVerifying = false
      state.errors.verify = errorMessage
    },
    updateRequest(state) {
      state.isUpdating = true
      state.errors.update = undefined
    },
    updateSuccess(state) {
      state.isUpdating = false
      state.errors.update = undefined
    },
    updateFailure(state, { payload: errorMessage }) {
      state.isUpdating = false
      state.errors.update = errorMessage
    },
    limitedProfileRequest(state) {
      state.isLoadingLimitedProfile = true
    },
    limitedProfileSuccess(state) {
      state.isLoadingLimitedProfile = false
    },
    limitedProfileFailure(state, { payload: errorMessage }) {
      state.isLoadingLimitedProfile = false
      state.errors.limitedProfile = errorMessage
    },
    setLimitedProfile(state, { payload: profile }) {
      state.limitedProfile = profile
    },
    clearLimitedProfile(state) {
      delete state.limitedProfile
    },
  },
})

export const {
  cancelFailure,
  cancelRequest,
  cancelSuccess,
  updateFailure,
  updateRequest,
  updateSuccess,
  deleteFailure,
  deleteRequest,
  deleteSuccess,
  downgradeToFree,
  logInFailure,
  logInRequest,
  logInSuccess,
  createRequest,
  createSuccess,
  createFailure,
  verifyRequest,
  verifyFailure,
  verifySuccess,
  logOut,
  setUserProfile,
  mergeUserProfile,
  setUserToken,
  setUserId,
  upgradeToPremium,
  reset,
  setLimitedProfile,
  clearLimitedProfile,
  limitedProfileRequest,
  limitedProfileFailure,
  limitedProfileSuccess,
} = slice.actions

export const loginWithEmail = ({ email, password }) => dispatch =>
  new Promise((resolve, reject) => {
    dispatch(logInRequest())

    auth.loginWithEmail(email, password).then(
      ({ userid, token }) => {
        dispatch(setUserId(userid))
        dispatch(setUserToken(token))
        dispatch(fetchUserProfile())
          .then(user => {
            trackLogin({ method: 'Email', user })
            dispatch(logInSuccess())
            resolve()
          })
          .catch(() => {
            dispatch(logInFailure())
            reject(messages.wentWrong)
          })
      },
      error => {
        dispatch(logInFailure())

        if (error && error.errorType) {
          if (error.errorType === 'invalid_email') {
            trackErrorMessage({
              type: 'Invalid email',
              category: 'Email login',
            })
            reject(messages.invalidEmail)
            return
          }
          if (error.errorType === 'invalid_pass') {
            trackErrorMessage({
              type: 'Invalid password',
              category: 'Email login',
            })
            reject(messages.incorrect)
            return
          }
        }

        reject(messages.wentWrong)
      }
    )
  })

export const loginWithFacebook = () => dispatch =>
  new Promise((resolve, reject) => {
    dispatch(logInRequest())

    auth.loginWithFacebook().then(
      ({ userid, token }) => {
        dispatch(setUserId(userid))
        dispatch(setUserToken(token))
        dispatch(fetchUserProfile())
          .then(user => {
            dispatch(logInSuccess())
            trackLogin({ method: 'Facebook', user })
            resolve()
          })
          .catch(() => {
            dispatch(logInFailure())
            reject(messages.wentWrong)
          })
      },
      error => {
        dispatch(logInFailure())
        if (error && error.status === 401) {
          trackErrorMessage({
            type: 'Account not connected',
            category: 'Facebook login',
          })
          reject(messages.facebookNotConnected)
        } else {
          trackErrorMessage({
            type: 'Something went wrong',
            category: 'Facebook login',
          })
          reject(messages.facebookWentWrong)
        }
      }
    )
  })

export const loginWithApple = () => dispatch =>
  new Promise((resolve, reject) => {
    dispatch(logInRequest())

    auth
      .loginWithApple()
      .then(({ userid, token }) => {
        dispatch(setUserId(userid))
        dispatch(setUserToken(token))
        dispatch(fetchUserProfile())
          .then(user => {
            dispatch(logInSuccess())
            trackLogin({ method: 'Apple', user })
            resolve()
          })
          .catch(() => {
            dispatch(logInFailure())
            reject(messages.wentWrong)
          })
      })
      .catch(error => {
        dispatch(logInFailure())
        if (error && error.code) {
          switch (error.code) {
            case USER_NOT_FOUND:
              trackErrorMessage({
                type: 'Account not connected',
                category: 'Sign in with Apple',
              })
              reject(messages.appleNotConnected)
              break
            case USER_CANCELLED:
              trackErrorMessage({
                type: 'User cancelled the request',
                category: 'Sign in with Apple',
              })
              reject(messages.appleWentWrong)
              break
            case UNKNOWN_ERROR:
            case INVALID_REQUEST:
            default:
              trackErrorMessage({
                type: 'Something went wrong',
                category: 'Sign in with Apple',
              })
              reject(messages.appleWentWrong)
          }
        } else {
          trackErrorMessage({
            type: 'Something went wrong',
            category: 'Sign in with Apple',
          })
          reject(messages.appleWentWrong)
        }
      })
  })

export const deleteAccount = () => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    dispatch(deleteRequest())

    auth.deleteAccount(getState().user.profile.id).then(
      () => {
        dispatch(logOut())
        dispatch(deleteSuccess())
        resolve()
      },
      () => {
        dispatch(deleteFailure())
        reject(messages.deleteWentWrong)
      }
    )
  })

export const cancelSubscription = () => dispatch =>
  new Promise((resolve, reject) => {
    dispatch(cancelRequest())

    auth.cancelSubscription().then(
      () => {
        dispatch(fetchUserProfile()).then(
          () => {
            dispatch(cancelSuccess())
            resolve()
          },
          () => {
            dispatch(cancelFailure(messages.cancelError))
            reject()
          }
        )
      },
      () => {
        dispatch(cancelFailure(messages.cancelError))
        reject()
      }
    )
  })

export const createAccount = ({ profile, language }) => dispatch =>
  new Promise((resolve, reject) => {
    dispatch(createRequest())

    return auth.createAccount(profile).then(
      ({ token, userid }) => {
        dispatch(setUserId(userid))
        dispatch(setUserToken(token))
        dispatch(fetchUserProfile()).then(
          user => {
            dispatch(createSuccess())
            trackCompleteRegistration({
              profile: { ...profile, id: user.id },
              type: 'Free',
              flowName: getSessionItem('signup-flowName'),
              method: getSessionItem('registration-method'),
              language,
              country: profile.country,
            })
            typeof hj !== 'undefined' &&
              hj('trigger', 'select_premium_subscription')
            resolve()
          },
          () => {
            dispatch(createFailure(messages.createError))
            reject()
          }
        )
      },
      () => {
        dispatch(createFailure(messages.createError))
        reject()
      }
    )
  })

export const verifyAccountEmail = code => dispatch =>
  new Promise((resolve, reject) => {
    dispatch(verifyRequest())
    dispatch(logOut())

    auth.verifyAccountEmail(code).then(
      ({ userid, token }) => {
        dispatch(setUserId(userid))
        dispatch(setUserToken(token))
        dispatch(fetchUserProfile())
          .then(() => {
            dispatch(verifySuccess())
            resolve()
          })
          .catch(() => {
            dispatch(verifyFailure(messages.wentWrong))
            reject()
          })
      },
      () => {
        dispatch(verifyFailure(messages.verifyError))
        reject()
      }
    )
  })

export const fetchUserProfile = () => dispatch =>
  new Promise((resolve, reject) => {
    auth.getUserProfile().then(
      profile => {
        const user = mapApiProfileToObject(profile)
        dispatch(setUserProfile(user))
        dispatch(clearLimitedProfile())
        resolve(user)
      },
      () => {
        dispatch(logOut())
        reject()
      }
    )
  })

export const updateUserProfile = ({
  birthDate: birthdate,
  firstName: firstname,
  gender,
  country,
}) => dispatch =>
  new Promise((resolve, reject) => {
    dispatch(updateRequest())

    auth
      .updateUserProfile({
        birthdate,
        firstname,
        gender,
        country,
      })
      .then(
        profile => {
          dispatch(mergeUserProfile(mapApiProfileToObject(profile)))
          dispatch(updateSuccess())
          resolve()
        },
        () => {
          dispatch(updateFailure(messages.updateError))
          reject()
        }
      )
  })

export const fetchLimitedAccountInfo = userId => dispatch => {
  dispatch(limitedProfileRequest())

  return new Promise((resolve, reject) => {
    auth.getAccountInfoByUserId(userId).then(
      res => {
        dispatch(
          setLimitedProfile({
            email: res.email,
            isPremium: res.is_gold,
            id: userId,
          })
        )
        dispatch(limitedProfileSuccess())
        dispatch(logOut())
        resolve()
      },
      () => {
        dispatch(limitedProfileFailure())
        reject()
      }
    )
  })
}

export const selectIsLoggedIn = createSelector(
  ['user.isLoggedIn'],
  isLoggedIn => isLoggedIn
)

export const selectIsPremium = createSelector(
  ['user.profile.isPremium'],
  isPremium => !!isPremium
)

export const selectIsOnFreeTrial = createSelector(
  ['user.profile.status'],
  status => status === 'trial'
)

export const selectHasCancelled = createSelector(
  ['user.profile.hasCancelled'],
  hasCancelled => !!hasCancelled
)

export const selectFirstName = createSelector(
  ['user.profile.firstName'],
  name => name
)

export const selectGender = createSelector(
  ['user.profile.gender'],
  gender => gender
)

export const selectBirthDate = createSelector(
  ['user.profile.birthDate'],
  date => (date ? new Date(date) : undefined)
)

export const selectAge = createSelector([selectBirthDate], date => {
  if (!date) return undefined
  return differenceInYears(Date.now(), date)
})

export const selectEmail = createSelector(
  ['user.profile.email'],
  email => email
)

export const selectProfileImage = createSelector(
  ['user.profile.image'],
  image => image
)

export const selectFacebookProfileImage = createSelector(
  ['user.profile.facebookImage'],
  image => image
)

export const selectCountry = createSelector(
  ['user.profile.country'],
  country => country
)

export const selectSubscriptionRenewalDate = createSelector(
  ['user.profile.autoRenewing', 'user.profile.expiresTime'],
  (willAutoRenew, expiresTime) => {
    if (!willAutoRenew) return undefined
    return new Date(expiresTime)
  }
)

export const selectSubscriptionExpiryTime = createSelector(
  ['user.profile.expiresTime'],
  time => (time ? new Date(time) : undefined)
)

export const selectSubscriptionStore = createSelector(
  ['user.profile.store'],
  store => store
)

export const selectSubscriptionLength = createSelector(
  ['user.profile.purchaseType'],
  type => type
)

export const selectPaymentMetaData = createSelector(
  ['user.profile.paymentMeta', 'user.profile.paymentMethod'],
  (meta, paymentMethod) => {
    return Object.assign({}, meta, { paymentMethod: paymentMethod })
  }
)

export const selectNextPaymentAmount = createSelector(
  ['user.profile.nextPaymentAmountLocal'],
  amount => amount
)

export const selectNextPaymentCurrency = createSelector(
  ['user.profile.currencyLocal'],
  currency => currency
)

export const selectLimitedProfile = createSelector(
  ['user.limitedProfile'],
  profile => profile
)

export const selectId = createSelector(['user.profile.id'], id => id)

export const selectIsAuthenticated = createSelector(['user.userToken'], token =>
  Boolean(token)
)

export const selectPurchaseTime = createSelector(
  ['user.profile.purchaseTime'],
  purchaseTime => purchaseTime
)

export const { reducer } = slice
